import _taggedTemplateLiteralLoose from "@babel/runtime/helpers/esm/taggedTemplateLiteralLoose";

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n  to {}\n"]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n  from {}\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}

// Autofill keyframes
import { useCallback, useState } from 'react';
import { isIE } from '../helpers/accessibility';
import { keyframes } from 'styled-components';
import { useNodeRef } from './useNodeRef';
import { useFunction } from './useFunction'; // According to: https://gist.github.com/jonathantneal/d462fc2bf761a10c9fca60eb634f6977

export var onAutoFillStart = keyframes(_templateObject());
export var onAutoFillCancel = keyframes(_templateObject2()); // Match the filter on autofilled elements in Firefox

var mozFilterMatch = /^grayscale\(.+\) brightness\((1)?.*\) contrast\(.+\) invert\(.+\) sepia\(.+\) saturate\(.+\)$/; // and: https://github.com/matteobad/detect-autofill/blob/master/src/detect-autofill.js

export var useAutofill = function useAutofill(initialRef, disabled) {
  if (disabled === void 0) {
    disabled = false;
  }

  var _useState = useState(false),
      isAutofilled = _useState[0],
      setAutofilled = _useState[1];

  var startAutofill = useCallback(function () {
    return setAutofilled(true);
  }, []);
  var endAutofill = useCallback(function () {
    return setAutofilled(false);
  }, []);

  var autofill = function autofill(target) {
    if (!target.hasAttribute('autofilled')) {
      target.setAttribute('autofilled', '');
      setTimeout(startAutofill);
    }
  };

  var cancelAutofill = function cancelAutofill(target) {
    if (target.hasAttribute('autofilled')) {
      target.removeAttribute('autofilled');
      setTimeout(endAutofill);
    }
  };

  var onAnimationStart = useFunction(function (event) {
    if (disabled) {
      return;
    } // detect autofills in Chrome and Safari by:
    //   - assigning animations to :-webkit-autofill, only available in Chrome and Safari
    //   - listening to animationstart for those specific animations


    if (event.animationName === onAutoFillStart.getName()) {
      // during autofill, the animation name is onAutoFillStart
      autofill(event.target);
    } else if (event.animationName === onAutoFillCancel.getName()) {
      // during autofill cancel, the animation name is onAutoFillCancel
      cancelAutofill(event.target);
    }
  });
  var onInput = useFunction(function (event) {
    var _event$target, _event$target2, _event$target3, _event$target4;

    if (disabled) {
      return;
    }

    var hasDataInEvent = 'data' in event || isIE(); // IE doesn't add `data` property even to normal events, so we simulating this

    var isReplacementEvent = (event == null ? void 0 : event.inputType) === 'insertReplacementText';
    var isClickEvent = ((_event$target = event.target) == null ? void 0 : _event$target.tagName) === 'SELECT';
    var isNotSupportedInput = ((_event$target2 = event.target) == null ? void 0 : _event$target2.tagName) === 'INPUT' && ((_event$target3 = event.target) == null ? void 0 : _event$target3.type) !== 'text' && ((_event$target4 = event.target) == null ? void 0 : _event$target4.type) !== 'password';

    if (!isNotSupportedInput && (isReplacementEvent || !hasDataInEvent && !isClickEvent)) {
      // input events without data or with insertReplacementText type are autofills
      autofill(event.target);
    } else {
      // input events with data may cancel autofills
      cancelAutofill(event.target);
    }
  });
  var onTransitionStart = useFunction(function (event) {
    if (disabled) {
      return;
    } // detect autofills in Firefox by:
    //   - listening to transitionstart, only available in Edge, Firefox, and Internet Explorer
    //   - checking filter style, which is only changed in Firefox


    var mozFilterTransition = event.propertyName === 'filter' && getComputedStyle(event.target).filter.match(mozFilterMatch);

    if (mozFilterTransition) {
      if (mozFilterTransition[1]) {
        // during autofill, brightness is 1
        autofill(event.target);
      } else {
        // during autofill cancel, brightness is not 1
        cancelAutofill(event.target);
      }
    }
  });

  var _useNodeRef = useNodeRef(initialRef, function (node) {
    node.addEventListener('animationstart', onAnimationStart, true);
    node.addEventListener('input', onInput, true);
    node.addEventListener('transitionstart', onTransitionStart, true);
  }),
      setRef = _useNodeRef[0],
      nodeRef = _useNodeRef[1];

  return {
    nodeRef: nodeRef,
    setRef: setRef,
    isAutofilled: isAutofilled
  };
};